import { useState } from "react";
import { HeartHandshake, Plus, AlertTriangle, CalendarClock, Users, ShieldAlert } from "lucide-react";
import { PageHeader, StatCard } from "../components/common/Ui";
import { JourneyHint } from "../components/common/Journey";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Button } from "../components/ui/button";
import { useFetch } from "../lib/hooks";
import { useAuth } from "../context/AuthContext";
import { PlacementTable, PlacementDialog } from "../components/hearing/Placements";
import { SessionDialog } from "../components/hearing/SessionDialog";
import { HearingMatrix } from "../components/hearing/HearingMatrix";
import { Behaviour } from "../components/hearing/Behaviour";
import { QuestionBank } from "../components/hearing/QuestionBank";
import { MasterData } from "../components/hearing/MasterData";

export default function Hearing() {
  const { can } = useAuth();
  const { data, reload } = useFetch("/hearing/overview");
  const [addOpen, setAddOpen] = useState(false);
  const [edit, setEdit] = useState(null);
  const [session, setSession] = useState(null); // {placementId, sessionId}
  const st = data?.stats;
  return (
    <div data-testid="hearing-page">
      <PageHeader title="Hearing & Consulting" subtitle="Pemantauan siswa yang sudah ditempatkan di Jepang — 36 bulan: tiap bulan (1–6), tiap 6 bulan (7–30), tiap bulan (31–36)."
        action={can("hearing:write") && <Button onClick={() => setAddOpen(true)} data-testid="hearing-add-button"><Plus className="h-4 w-4 mr-1" />Tambah penempatan</Button>} />
      <JourneyHint k="hearing" />
      {st && <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 mb-5">
        <StatCard label="Siswa dipantau" value={st.total} icon={Users} tone="indigo" testId="hearing-stat-total" />
        <StatCard label="Aktif di Jepang" value={st.aktif} icon={HeartHandshake} tone="emerald" />
        <StatCard label="Hearing terlambat" value={st.late} icon={AlertTriangle} tone={st.late ? "red" : "slate"} testId="hearing-stat-late" />
        <StatCard label="Sesi selesai" value={st.done_sessions} icon={CalendarClock} tone="sky" />
        <StatCard label="Risiko tinggi" value={st.risk_high} icon={ShieldAlert} tone={st.risk_high ? "red" : "slate"} />
      </div>}
      <Tabs defaultValue="list">
        <TabsList className="flex-wrap h-auto">
          <TabsTrigger value="list" data-testid="hearing-tab-list">Daftar penempatan</TabsTrigger>
          <TabsTrigger value="month" data-testid="hearing-tab-month">Bulan ini{st?.late ? <span className="ml-1.5 rounded-full bg-red-600 text-white text-[10px] px-1.5">{st.late}</span> : null}</TabsTrigger>
          <TabsTrigger value="matrix" data-testid="hearing-tab-matrix">Matriks asesmen</TabsTrigger>
          <TabsTrigger value="behaviour" data-testid="hearing-tab-behaviour">Student Behaviour</TabsTrigger>
          <TabsTrigger value="questions" data-testid="hearing-tab-questions">Bank pertanyaan</TabsTrigger>
          <TabsTrigger value="master" data-testid="hearing-tab-master">Perusahaan & Agency</TabsTrigger>
        </TabsList>
        <TabsContent value="list"><PlacementTable data={data} onEdit={setEdit} onOpenSession={(pid, sid) => setSession({ placementId: pid, sessionId: sid })} onChanged={reload} /></TabsContent>
        <TabsContent value="month">
          <div className="surface p-4" data-testid="hearing-month">
            <h3 className="section-title mb-2">Hearing jatuh tempo bulan ini & yang terlambat</h3>
            {!data?.this_month?.length ? <p className="text-sm text-slate-400">Tidak ada hearing bulan ini.</p> : (
              <ul className="divide-y divide-slate-100 dark:divide-slate-800">{data.this_month.map((s) => (
                <li key={s.id} className="py-2 flex flex-wrap items-center gap-2 text-sm">
                  <span className={`h-2.5 w-2.5 rounded-full ${s.status === "done" ? "bg-emerald-500" : s.status === "late" ? "bg-red-500" : "bg-amber-400"}`} />
                  <span className="font-mono text-xs">{s.due_date}</span><span className="font-medium">{s.student_name}</span>
                  <span className="text-xs text-slate-500">Sesi #{s.seq} · Fase {s.phase} · bulan ke-{s.month_no}</span>
                  <span className={`text-[10px] px-1.5 rounded-full ${s.status === "late" ? "bg-red-100 text-red-700" : s.status === "done" ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-800"}`}>{s.status === "late" ? "Terlambat" : s.status === "done" ? "Selesai" : "Terjadwal"}</span>
                  <Button size="sm" variant="outline" className="ml-auto h-7 text-xs" onClick={() => setSession({ placementId: s.placement_id, sessionId: s.id })} data-testid="hearing-month-open">Buka</Button>
                </li>))}</ul>
            )}
          </div>
        </TabsContent>
        <TabsContent value="matrix"><HearingMatrix data={data} onOpenSession={(pid, sid) => setSession({ placementId: pid, sessionId: sid })} /></TabsContent>
        <TabsContent value="behaviour"><Behaviour onOpenSession={(pid, sid) => setSession({ placementId: pid, sessionId: sid })} /></TabsContent>
        <TabsContent value="questions"><QuestionBank /></TabsContent>
        <TabsContent value="master"><MasterData /></TabsContent>
      </Tabs>
      {(addOpen || edit) && <PlacementDialog initial={edit} onClose={() => { setAddOpen(false); setEdit(null); }} onDone={reload} />}
      {session && <SessionDialog {...session} onClose={() => setSession(null)} onChanged={reload} />}
    </div>
  );
}
